import { Injectable } from '@angular/core';
import { clearTimeouts } from '@utils/clear-timeouts';
import { µsetTimeout } from '@utils/function/timer';
import tippy, { Instance, Placement, Props } from 'tippy.js';

export interface TippyInstance extends Instance<Props> {
  guid: string;
};

export type TooltipAnimation =
  true
  | false
  | 'shift-away'
  | 'shift-toward'
  | 'scale'
  | 'perspective'
  // added via css
  | 'scale-subtle';

// added via css
export type TooltipTheme =
  'translucent'
  | 'light'
  | 'light-opaque';

export const TOOLTIP_DEFAULT_ALLOW_HTML: boolean = true as const;
export const TOOLTIP_DEFAULT_THEME: TooltipTheme = 'translucent' as const;
export const TOOLTIP_DEFAULT_OFFSET: [number, number] = [0, 0] as const;
export const TOOLTIP_DEFAULT_PLACEMENT: Placement = 'auto' as const;
export const TOOLTIP_DEFAULT_ARROW: boolean = true as const;
export const TOOLTIP_DEFAULT_INERTIA: boolean = true as const;
export const TOOLTIP_DEFAULT_INTERACTIVE: boolean = false as const;
export const TOOLTIP_DEFAULT_MAXWIDTH: number | 'none' = 'none' as const;
export const TOOLTIP_DEFAULT_ANIMATION: TooltipAnimation = true as const;
export const TOOLTIP_DEFAULT_DELAY: number | [number | null, number | null] = [500, 0] as const;
export const TOOLTIP_DEFAULT_DURATION: number | [number | null, number | null] = [0, 0] as const;

export const TOOLTIP_DEFAULT_OPTIONS: Partial<Props> = {
  // never hide upon clicking
  hideOnClick: false,
  // only programmatically trigger it
  trigger: 'manual',
  /* 🧰 our theme */
  theme: TOOLTIP_DEFAULT_THEME,
  allowHTML: TOOLTIP_DEFAULT_ALLOW_HTML,
  delay: TOOLTIP_DEFAULT_DELAY,
  duration: TOOLTIP_DEFAULT_DURATION,
  placement: TOOLTIP_DEFAULT_PLACEMENT,
  offset: TOOLTIP_DEFAULT_OFFSET,
  arrow: TOOLTIP_DEFAULT_ARROW,
  animation: TOOLTIP_DEFAULT_ANIMATION,
  inertia: TOOLTIP_DEFAULT_INERTIA,
  interactive: TOOLTIP_DEFAULT_INTERACTIVE,
  maxWidth: TOOLTIP_DEFAULT_MAXWIDTH
} as const;

@Injectable({ providedIn: 'root' })
export class TooltipService {

  readonly #tooltipList = new Set<TippyInstance>();
  #currentTooltipShown: TippyInstance;
  #showTimerId = new Map<string, number>();

  constructor() { }

  createTooltip(guid: string, target: Element, tooltip: string, options?: Partial<Props>): TippyInstance {
    if (!target) { throw new Error('Target element for the tooltip is not defined'); }

    const instance = {
      ...tippy(target, this.getTippyOptions(tooltip, options)),
      guid
    } as TippyInstance;

    this.#tooltipList.add(instance);

    return instance;
  }

  showTooltip(instance: TippyInstance) {
    const timerId = this.#showTimerId.get(instance.guid);

    if (timerId) { return; }

    if (this.#currentTooltipShown && instance.guid !== this.#currentTooltipShown.guid) { this.#currentTooltipShown.hide(); }

    this.#currentTooltipShown = instance;

    this.#showTimerId.set(
      instance.guid,
      µsetTimeout(() => {
        instance.show();
      }, getDelayValue(instance, 'Show'))
    );

  }

  hideTooltip(instance: TippyInstance) {
    const currentTimerId = this.#currentTooltipShown && this.#showTimerId.get(this.#currentTooltipShown.guid);

    if (currentTimerId) {
      clearTimeouts([currentTimerId]);
      this.#showTimerId.set(this.#currentTooltipShown.guid, undefined);
    }

    const timerId = this.#showTimerId.get(instance.guid);

    if (timerId) {
      clearTimeouts([timerId]);
      this.#showTimerId.set(instance.guid, undefined);
    }

    this.#currentTooltipShown = null;
    instance.hide();

  }

  toggleTooltip(instance: TippyInstance) {
    if (instance.state.isVisible) {
      this.hideTooltip(instance);
    } else {
      this.showTooltip(instance);
    }
  }

  enable(instance: TippyInstance) {
    if (instance.state.isEnabled) { return; }

    instance.enable();
  }

  disable(instance: TippyInstance) {
    if (!instance.state.isEnabled) { return; }

    instance.disable();
  }

  unmount(instance: TippyInstance) {
    instance.unmount();
  }

  destroy(instance: TippyInstance) {
    this.#tooltipList.delete(instance);
    instance.destroy();
  }

  hideAll() {
    this.#showTimerId.forEach((timerId) => {
      if (timerId) { clearTimeouts([timerId]); }
    });
    this.#showTimerId.clear();
    this.#currentTooltipShown = null;

    this.#tooltipList.forEach(instance => {
      instance.hide();
    });
  }

  private getTippyOptions(tooltip: string, options?: Partial<Props>) {
    const tippyOptions = { ...TOOLTIP_DEFAULT_OPTIONS, ...options };

    tippyOptions.content = getContentFinal(tooltip);

    return tippyOptions;
  }
}

function getContentFinal(tooltip: string) {
  return `${tooltip}`;
}

function getDelayValue(instance: TippyInstance, state: 'Show' | 'Hidde'): number {
  let delay = instance.props.delay;

  if (delay instanceof Array) { delay = delay[state === 'Show' ? 0 : 1] || 0; }

  return delay || 0;
}
